import {motion} from 'framer-motion';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, Icon, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {DeepPartial} from 'react-hook-form';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useSendCustomerEmailMutation} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {CustomerSelectField} from './components/CustomerSelectField';
import {EmailPreview} from './components/EmailPreview';
import {MessageFormField} from './components/MessageFormField';
import {MessageUpload} from './components/MessageUpload';
import {useSmtpOptions} from './hooks/useSmtpOptions';
import {FormValues, SendCustomerEmailFormSchema} from './types/SendCustomerEmailFormSchema';
import {UploadedCustomerMessageFile} from './types/UploadedCustomerMessageFile';
import {getCustomerEmails} from './utils/getCustomerEmails';

export interface SentEmailToCustomerFormProps {
  // SHOULD be always present, but form can function without them as "general send an email action"
  customerId?: string | Nullish;
  recordId?: string | Nullish;
  resourceId?: EntityResourceIds | Nullish;
  vehicleId?: string | Nullish;
  defaultValues?: {
    attachments?: UploadedCustomerMessageFile[];
    customerEmailAddress?: string | Nullish;
    subject?: string | Nullish;
    body?: string | Nullish;
  };
}

export function SendCustomerEmailForm(props: SentEmailToCustomerFormProps) {
  const {defaultSmtpCredentialId, isLoading: isLoadingSmtpOptions} =
    useSmtpOptions('customer-message');

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {
      customerId: props.customerId ?? '',
    },
    {skip: isNilOrEmpty(props.customerId)}
  );

  const [sendCustomerMessage] = useSendCustomerEmailMutation();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedCustomerMessageFile[]>(
    props.defaultValues?.attachments ?? []
  );

  const defaultValues: DeepPartial<FormValues> = {
    customerId: props.customerId ?? undefined,
    customerEmailAddress:
      props.defaultValues?.customerEmailAddress ?? getCustomerEmails(customer)[0]?.value,
    subject: props.defaultValues?.subject ?? undefined,
    body: props.defaultValues?.body ?? undefined,
    customerMessageId: ['customer-message'],
    smtpCredentialsId: defaultSmtpCredentialId,
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (data) => {
    const attachments = isNotNilOrEmpty(uploadedFiles)
      ? uploadedFiles.map((file) => ({
          fileId: file.fileId,
          convertToPdf: file.shouldConvertToPdf,
        }))
      : undefined;

    await sendCustomerMessage({
      sendCustomerEmailRequestBody: {
        body: data.body ?? '',
        subject: data.subject ?? '',
        customerId: data.customerId,
        customerEmailAddress: data.customerEmailAddress,
        copyTo: data.copyTo,
        attachments,
        recordId: props.recordId ?? null,
        resourceId: props.resourceId ?? null,
        smtpCredentialsId: data.smtpCredentialsId,
      },
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.customer.labels.successfullySend')))
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const isLoading = isLoadingCustomer || isLoadingSmtpOptions;

  return (
    <DataStatus isLoading={isLoading} isError={isCustomerError}>
      <Box position="relative">
        <motion.div>
          <Form<FormValues>
            defaultValues={defaultValues}
            experimentalZodSchema={SendCustomerEmailFormSchema}
            onSubmit={handleSubmit}
          >
            {(control, formApi) => (
              <Grid columns={[1, 1, 2]}>
                <VStack spacing={2}>
                  <Box paddingHorizontal={[4, 4, 10]}>
                    <VStack spacing={4} align="center" justify="center">
                      <Icon
                        value="action/mail_upload"
                        color="palettes.blue.30.100"
                        isNotFilled
                        size={20}
                      />

                      <Heading size={3}>
                        {i18n.t('entity.customer.labels.customer_message_title')}
                      </Heading>
                      <Text color="secondary" align="center">
                        {i18n.t('entity.customer.labels.customer_message_title_description')}
                      </Text>
                    </VStack>
                  </Box>

                  <CustomerSelectField
                    control={control}
                    formApi={formApi}
                    customerMessageId="customer-message"
                  />

                  <VStack spacing={4}>
                    <FormField
                      control={control}
                      name="subject"
                      type="text"
                      label={i18n.t('general.labels.subject')}
                      isRequired
                    />

                    <MessageFormField
                      control={control}
                      formApi={formApi}
                      resourceId={props.resourceId}
                      recordId={props.recordId}
                    />
                    <MessageUpload
                      vehicleId={props.vehicleId}
                      resourceId={props.resourceId}
                      recordId={props.recordId}
                      uploadedFiles={uploadedFiles}
                      setUploadedFiles={setUploadedFiles}
                    />
                  </VStack>
                </VStack>

                <EmailPreview formApi={formApi} control={control} />

                <GridItem span={[1, 1, 2]}>
                  <ButtonGroup align="right">
                    <Button
                      onClick={closeCurrentDialog}
                      variant="secondary"
                      title={i18n.t('general.actions.cancel')}
                    />

                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.send')}
                    />
                  </ButtonGroup>
                </GridItem>
              </Grid>
            )}
          </Form>
        </motion.div>
      </Box>
    </DataStatus>
  );
}
